/**
 * Статусы зомби от героев (E3): заморозка, замедление, горение, шок.
 *
 * Сроки — в `ZombieEntity.data` (тик волны, до которого статус действует; входит в хэш), отсюда выводятся
 * `speedPermille` (его читает ядро) и `statuses` (для рендера). Общее обновление зовёт каждый герой на своём тике,
 * выполняется оно один раз за тик (повтор ничего бы не изменил).
 */
import type { StatusKind } from "@newfungame/contracts";
import { PERMILLE } from "@newfungame/contracts";
import type { SimApi, ZombieEntity } from "../sim/types";

const FROZEN_UNTIL = "frozenUntil";
const FROZEN_CUT = "frozenCut";
const SLOW_UNTIL = "slowUntil";
const SLOW_CUT = "slowCut";
const BURN_UNTIL = "burnUntil";
const SHOCK_UNTIL = "shockUntil";

function extend(z: ZombieEntity, untilKey: string, cutKey: string | null, until: number, cut: number): void {
  const d = z.data;
  d[untilKey] = Math.max(d[untilKey] ?? 0, until);
  if (cutKey) d[cutKey] = Math.max(d[cutKey] ?? 0, cut);
}

/** Заморозка на `ticks` тиков: скорость × (1000 − `cutPermille`) ‰; при 1000 — стоит и не бьёт. */
export function freezeZombie(z: ZombieEntity, tick: number, ticks: number, cutPermille: number): void {
  if (ticks <= 0) return;
  extend(z, FROZEN_UNTIL, FROZEN_CUT, tick + ticks, clampCut(cutPermille));
  refreshStatus(z, tick);
}

/** Замедление на `ticks` тиков на `cutPermille` ‰ (500 — вдвое медленнее). Сильнейшее и самое долгое побеждает. */
export function slowZombie(z: ZombieEntity, tick: number, ticks: number, cutPermille: number): void {
  if (ticks <= 0 || cutPermille <= 0) return;
  extend(z, SLOW_UNTIL, SLOW_CUT, tick + ticks, clampCut(cutPermille));
  refreshStatus(z, tick);
}

/** Пометка «горит» (огонь Blaze) — только вид. */
export function markBurning(z: ZombieEntity, tick: number, ticks: number): void {
  extend(z, BURN_UNTIL, null, tick + ticks, 0);
  refreshStatus(z, tick);
}

/** Пометка «шок» (молния Sparky) — только вид. */
export function markShocked(z: ZombieEntity, tick: number, ticks: number): void {
  extend(z, SHOCK_UNTIL, null, tick + ticks, 0);
  refreshStatus(z, tick);
}

function clampCut(cut: number): number {
  return Math.max(0, Math.min(PERMILLE, cut));
}

const ORDER: readonly StatusKind[] = ["burning", "frozen", "slowed", "shocked"];

function maskOf(list: readonly StatusKind[]): number {
  let m = 0;
  for (const k of list) m |= 1 << ORDER.indexOf(k);
  return m;
}

/** Пересчитать `speedPermille` и `statuses` зомби на тике `tick`, убрать истёкшие сроки. */
export function refreshStatus(z: ZombieEntity, tick: number): void {
  const d = z.data;
  const active = (untilKey: string, cutKey: string | null): boolean => {
    const until = d[untilKey];
    if (until === undefined) return false;
    if (until > tick) return true;
    delete d[untilKey];
    if (cutKey) delete d[cutKey];
    return false;
  };
  const burning = active(BURN_UNTIL, null);
  const frozen = active(FROZEN_UNTIL, FROZEN_CUT);
  const slowed = active(SLOW_UNTIL, SLOW_CUT);
  const shocked = active(SHOCK_UNTIL, null);
  let speed = PERMILLE;
  if (frozen) speed = PERMILLE - d[FROZEN_CUT]!;
  else if (slowed) speed = PERMILLE - d[SLOW_CUT]!;
  z.speedPermille = speed;
  const mask = (burning ? 1 : 0) | (frozen ? 2 : 0) | (slowed && !frozen ? 4 : 0) | (shocked ? 8 : 0);
  if (mask !== maskOf(z.statuses)) z.statuses = ORDER.filter((_, i) => (mask & (1 << i)) !== 0);
}

/** Тик последнего общего обновления по бою: герои зовут его каждый, считается один раз за тик. */
const refreshed = new WeakMap<SimApi, number>();

/** Обновить статусы всех живых зомби (у кого они есть) — один раз за тик боя. */
export function refreshAllStatuses(sim: SimApi): void {
  const tick = sim.tick;
  if (refreshed.get(sim) === tick) return;
  refreshed.set(sim, tick);
  for (const z of sim.zombies()) {
    if (z.statuses.length > 0 || z.speedPermille !== PERMILLE) refreshStatus(z, tick);
  }
}
